import { useEffect, useMemo, useState } from 'react';
import { listarMotores } from '../lib/api';
import { CONTROLES_TEMPO } from '../lib/controles';
import { CATALOGO_NIVEIS } from '../lib/niveis';
import type {
  ConfiguracaoJogo, Cor, EstiloModelo, LadoAutomatico, ModeloPublico, ModoJogo, MotorLocal, MotorPublico, NivelClassico, Preferencias, TipoLado
} from '../types';
import { Icone } from './Icone';
import { Peca } from './Peca';

interface Props {
  modo: ModoJogo;
  modelos: ModeloPublico[];
  preferencias?: Preferencias;
  aoVoltar: () => void;
  aoIniciar: (config: ConfiguracaoJogo) => void;
  aoAdministrar: () => void;
  nomeJogador?: string;
}

const titulos: Record<ModoJogo, { titulo: string; frase: string }> = {
  local: { titulo: 'Jogador vs Jogador local', frase: 'Prepare a mesa para uma disputa no mesmo aparelho.' },
  classico: { titulo: 'Jogador vs CPU', frase: 'Escolha o nível do Stockfish e a sua cor.' },
  modelo: { titulo: 'Modelo de linguagem', frase: 'Laboratório · escolha um modelo autorizado para a partida.' },
  'duelo-motores': { titulo: 'CPU vs CPU', frase: 'Dois níveis de Stockfish jogam entre si — você assiste.' },
  'duelo-modelos': { titulo: 'Duelo de modelos', frase: 'Laboratório · dois LLMs se enfrentam no tabuleiro.' },
  benchmark: { titulo: 'Benchmark assistido', frase: 'Laboratório · série de partidas com placar.' },
  online: { titulo: 'Jogador vs Jogador online', frase: 'Use a sala multiplayer para desafiar conectados.' }
};

const niveis: Array<{ id: NivelClassico; nome: string; detalhe: string }> = CATALOGO_NIVEIS.map((n) => ({
  id: n.id,
  nome: n.nome,
  detalhe: n.detalhe
}));
const assistencias: Array<{ id: 'puro' | 'orientado' | 'regio'; nome: string; detalhe: string }> = [
  { id: 'puro', nome: 'Puro', detalhe: 'Só o modelo' },
  { id: 'orientado', nome: 'Orientado', detalhe: 'Top 10 do SF' },
  { id: 'regio', nome: 'Precisão régia', detalhe: 'Qualidade alta' }
];

const estilos: Array<{ id: EstiloModelo; nome: string }> = [
  { id: 'cauteloso', nome: 'Cauteloso' },
  { id: 'equilibrado', nome: 'Equilibrado' },
  { id: 'agressivo', nome: 'Agressivo' },
  { id: 'criativo', nome: 'Criativo' }
];

function nomeMotor(tipo: TipoLado, nivel: NivelClassico, modeloNome?: string, papel?: 'A' | 'B'): string {
  const prefixo = papel ? `${papel} · ` : '';
  if (tipo === 'modelo') return `${prefixo}${modeloNome || 'Modelo'}`;
  if (tipo === 'lc0') return `${prefixo}Leela · ${nivel}`;
  return `${prefixo}Stockfish · ${nivel}`;
}

export function PrepararPartida({ modo, modelos, preferencias, aoVoltar, aoIniciar, aoAdministrar, nomeJogador }: Props) {
  const disponiveis = useMemo(() => modelos.filter((m) => m.disponivel), [modelos]);
  const controleSalvo = preferencias?.controlePreferido;
  const controleInicial =
    modo === 'benchmark' || modo.startsWith('duelo')
      ? '5-0'
      : controleSalvo && CONTROLES_TEMPO.some((c) => c.id === controleSalvo)
        ? controleSalvo
        : '10-0';
  const nivelInicial: NivelClassico =
    modo === 'classico'
      ? preferencias?.nivelPreferido ?? '400'
      : modo === 'duelo-motores' || modo === 'benchmark'
        ? '2500'
        : '1600';
  const [cor, setCor] = useState<Cor | 'aleatoria'>('w');
  const [controleId, setControleId] = useState(controleInicial);
  const [minutosPersonalizados, setMinutosPersonalizados] = useState(preferencias?.minutosPersonalizados ?? 5);
  const [incrementoPersonalizado, setIncrementoPersonalizado] = useState(preferencias?.incrementoPersonalizado ?? 0);
  const [nivel, setNivel] = useState<NivelClassico>(nivelInicial);
  const [estilo, setEstilo] = useState<EstiloModelo>('equilibrado');
  const [assistencia, setAssistencia] = useState<'puro' | 'orientado' | 'regio'>('regio');
  const [motorLocal] = useState<MotorLocal>('stockfish');
  const [motores, setMotores] = useState<MotorPublico[]>([]);
  const [modeloId, setModeloId] = useState(disponiveis.find((m) => m.padrao)?.idPublico || disponiveis[0]?.idPublico || '');
  const [desfazer, setDesfazer] = useState(modo === 'local' || modo === 'classico' || modo === 'modelo');
  const [inverter, setInverter] = useState(false);
  const [nomeUm, setNomeUm] = useState(nomeJogador?.trim() || 'Jogador 1');
  const [nomeDois, setNomeDois] = useState('Jogador 2');

  // Duelos / benchmark (CPU vs CPU usa só Stockfish; lab de IA mantém tipos)
  const [tipoA, setTipoA] = useState<TipoLado>(modo === 'duelo-modelos' ? 'modelo' : 'stockfish');
  const [tipoB, setTipoB] = useState<TipoLado>(modo === 'duelo-modelos' ? 'modelo' : modo === 'duelo-motores' ? 'stockfish' : 'stockfish');
  const [nivelA, setNivelA] = useState<NivelClassico>(modo === 'duelo-motores' ? '2200' : '2000');
  const [nivelB, setNivelB] = useState<NivelClassico>(modo === 'duelo-motores' ? '2500' : '2000');
  const [modeloA, setModeloA] = useState(disponiveis[0]?.idPublico || '');
  const [modeloB, setModeloB] = useState(disponiveis[1]?.idPublico || disponiveis[0]?.idPublico || '');
  const [estiloA, setEstiloA] = useState<EstiloModelo>('equilibrado');
  const [estiloB, setEstiloB] = useState<EstiloModelo>('agressivo');
  const [partidasSerie, setPartidasSerie] = useState(4);
  const [limiteLances, setLimiteLances] = useState(200);
  const [inverterCores, setInverterCores] = useState(true);

  // Lab de IA ainda pode listar motores; menu público usa só Stockfish.
  useEffect(() => {
    if (modo !== 'modelo' && modo !== 'duelo-modelos' && modo !== 'benchmark') return;
    void listarMotores()
      .then(setMotores)
      .catch(() => setMotores([]));
  }, [modo]);

  const lc0 = motores.find((m) => m.id === 'lc0');
  const cpuVsCpu = modo === 'duelo-motores';
  const controleBase = CONTROLES_TEMPO.find((c) => c.id === controleId) ?? CONTROLES_TEMPO[6];
  const minutosValidos = Math.min(180, Math.max(1, Math.round(minutosPersonalizados || 1)));
  const incrementoValido = Math.min(60, Math.max(0, Math.round(incrementoPersonalizado || 0)));
  const controle =
    controleId === 'personalizado'
      ? { id: 'personalizado', nome: `Personalizado · ${minutosValidos} + ${incrementoValido}`, minutos: minutosValidos, incremento: incrementoValido }
      : controleBase;
  const modeloSelecionado = disponiveis.find((m) => m.idPublico === modeloId);
  const modeloSelA = disponiveis.find((m) => m.idPublico === modeloA);
  const modeloSelB = disponiveis.find((m) => m.idPublico === modeloB);

  function montarLado(
    papel: 'A' | 'B',
    tipo: TipoLado,
    nivelL: NivelClassico,
    modeloPub?: string,
    est?: EstiloModelo,
    modeloNome?: string
  ): LadoAutomatico {
    return {
      tipo,
      papel,
      nome: nomeMotor(tipo, nivelL, modeloNome, papel),
      nivel: nivelL,
      modeloPublicoId: tipo === 'modelo' ? modeloPub : undefined,
      estilo: tipo === 'modelo' ? est : undefined
    };
  }

  function trocarParticipantes() {
    setTipoA(tipoB);
    setTipoB(tipoA);
    setNivelA(nivelB);
    setNivelB(nivelA);
    setModeloA(modeloB);
    setModeloB(modeloA);
    setEstiloA(estiloB);
    setEstiloB(estiloA);
  }

  function iniciar() {
    if (modo === 'modelo' && !modeloId) return;
    if (modo === 'classico' && motorLocal === 'lc0' && !lc0?.disponivel) return;

    if (modo === 'duelo-motores' || modo === 'duelo-modelos' || modo === 'benchmark') {
      // Menu público CPU vs CPU: sempre Stockfish com níveis.
      const tA: TipoLado = cpuVsCpu ? 'stockfish' : tipoA;
      const tB: TipoLado = cpuVsCpu ? 'stockfish' : tipoB;
      if (tA === 'lc0' || tB === 'lc0') {
        if (!lc0?.disponivel) return;
      }
      if (tA === 'modelo' && !modeloA) return;
      if (tB === 'modelo' && !modeloB) return;

      // A = brancas, B = pretas (sempre — ordem escolhida pelo usuário).
      const ladoA = montarLado('A', tA, nivelA, modeloA, estiloA, modeloSelA?.nome);
      const ladoB = montarLado('B', tB, nivelB, modeloB, estiloB, modeloSelB?.nome);
      // Duelo avulso = série de 1; benchmark = N partidas. Sempre grava no arquivo de séries.
      const totalSerie =
        modo === 'benchmark' ? Math.min(32, Math.max(1, Math.round(partidasSerie || 1))) : 1;
      const serie = {
        idSerie: crypto.randomUUID(),
        total: totalSerie,
        indice: 0,
        pontosA: 0,
        pontosB: 0,
        empates: 0,
        rotuloA: ladoA.nome,
        rotuloB: ladoB.nome,
        inverterACadaPartida: modo === 'benchmark' ? inverterCores : false
      };

      aoIniciar({
        modo,
        corJogador: 'w',
        controle,
        nivel: nivelA,
        estilo: estiloA,
        brancasAuto: ladoA,
        pretasAuto: ladoB,
        apenasAssistir: true,
        limiteLances: Math.min(600, Math.max(20, Math.round(limiteLances || 200))),
        serie,
        permitirDesfazer: false,
        inverterAutomaticamente: false,
        nomeBrancas: ladoA.nome,
        nomePretas: ladoB.nome
      });
      return;
    }

    const corResolvida: Cor = cor === 'aleatoria' ? (Math.random() > 0.5 ? 'w' : 'b') : cor;
    const eu = nomeJogador?.trim() || nomeUm.trim() || 'Você';
    // Menu público: adversário CPU é sempre Stockfish.
    const adversario =
      modo === 'classico'
        ? `Stockfish · ${niveis.find((n) => n.id === nivel)?.nome || nivel}`
        : modeloSelecionado?.nome || 'Modelo convidado';
    aoIniciar({
      modo,
      corJogador: corResolvida,
      controle,
      nivel,
      estilo,
      assistenciaModelo: modo === 'modelo' ? assistencia : undefined,
      motorLocal: 'stockfish',
      modeloPublicoId: modo === 'modelo' ? modeloId || undefined : undefined,
      permitirDesfazer: desfazer,
      inverterAutomaticamente: modo === 'local' && inverter,
      nomeBrancas: modo === 'local' ? nomeUm.trim() || 'Jogador 1' : corResolvida === 'w' ? eu : adversario,
      nomePretas: modo === 'local' ? nomeDois.trim() || 'Jogador 2' : corResolvida === 'b' ? eu : adversario
    });
  }

  const duelo = modo === 'duelo-motores' || modo === 'duelo-modelos' || modo === 'benchmark';
  const tiposPermitidosA: TipoLado[] =
    modo === 'duelo-modelos' ? ['modelo'] : modo === 'duelo-motores' ? ['stockfish'] : ['stockfish', 'lc0', 'modelo'];
  const tiposPermitidosB = tiposPermitidosA;

  function seletorTipo(valor: TipoLado, set: (t: TipoLado) => void, permitidos: TipoLado[]) {
    return (
      <div className="seletor-segmentado tres">
        {permitidos.includes('stockfish') && (
          <button type="button" className={valor === 'stockfish' ? 'selecionado' : ''} onClick={() => set('stockfish')}>
            Stockfish
          </button>
        )}
        {permitidos.includes('lc0') && (
          <button type="button" className={valor === 'lc0' ? 'selecionado' : ''} disabled={!lc0?.disponivel} onClick={() => lc0?.disponivel && set('lc0')}>
            Leela
          </button>
        )}
        {permitidos.includes('modelo') && (
          <button type="button" className={valor === 'modelo' ? 'selecionado' : ''} disabled={!disponiveis.length} onClick={() => disponiveis.length && set('modelo')}>
            Modelo
          </button>
        )}
      </div>
    );
  }

  function blocoLado(
    papel: 'A' | 'B',
    corLado: 'Brancas' | 'Pretas',
    tipo: TipoLado,
    setTipo: (t: TipoLado) => void,
    nivelL: NivelClassico,
    setNivelL: (n: NivelClassico) => void,
    modelo: string,
    setModelo: (id: string) => void,
    est: EstiloModelo,
    setEst: (e: EstiloModelo) => void,
    permitidos: TipoLado[]
  ) {
    return (
      <section className="grupo-preparacao" data-papel={papel} data-cor={corLado.toLowerCase()}>
        <div className="titulo-grupo">
          <span>{papel}</span>
          <div>
            <strong>Participante {papel} · {corLado}</strong>
            <small>{corLado === 'Brancas' ? 'Joga primeiro (peças claras)' : 'Responde (peças escuras)'}</small>
          </div>
        </div>
        {seletorTipo(tipo, setTipo, permitidos)}
        {(tipo === 'stockfish' || tipo === 'lc0') && (
          <label className="campo-regio" style={{ marginTop: '0.75rem' }}>
            <span>Nível</span>
            <select value={nivelL} onChange={(e) => setNivelL(e.target.value as NivelClassico)}>
              {niveis.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.nome} — {n.detalhe}
                </option>
              ))}
            </select>
          </label>
        )}
        {tipo === 'modelo' && (
          <>
            <label className="campo-regio" style={{ marginTop: '0.75rem' }}>
              <span>Modelo</span>
              <select value={modelo} onChange={(e) => setModelo(e.target.value)}>
                {disponiveis.map((m) => (
                  <option key={m.idPublico} value={m.idPublico}>
                    {m.provedor} · {m.nome}
                  </option>
                ))}
              </select>
            </label>
            <div className="seletor-segmentado quatro" style={{ marginTop: '0.75rem' }}>
              {estilos.map((item) => (
                <button type="button" key={item.id} className={est === item.id ? 'selecionado' : ''} onClick={() => setEst(item.id)}>
                  {item.nome}
                </button>
              ))}
            </div>
          </>
        )}
      </section>
    );
  }

  const previewA = nomeMotor(tipoA, nivelA, modeloSelA?.nome, 'A');
  const previewB = nomeMotor(tipoB, nivelB, modeloSelB?.nome, 'B');

  const podeIniciarDuelo =
    !(tipoA === 'lc0' || tipoB === 'lc0') || Boolean(lc0?.disponivel);
  const modelosOk =
    (tipoA !== 'modelo' || Boolean(modeloA)) && (tipoB !== 'modelo' || Boolean(modeloB)) && (modo !== 'duelo-modelos' || disponiveis.length >= 1);

  return (
    <main className="tela-preparar">
      <header className="cabecalho-interno">
        <button type="button" className="botao-voltar" onClick={aoVoltar}>
          <Icone nome="voltar" /> Voltar
        </button>
        <div className="monograma-interno">
          <Icone nome="coroa" tamanho={23} />
          <span>XADREZ</span>
        </div>
        <span className="etapa-interno">PREPARAÇÃO</span>
      </header>
      <section className="preparacao-conteudo">
        <aside className="preparacao-cena" aria-hidden="true">
          <div className="arco-cena" />
          <div className="pedestal-peca">
            <Peca tipo={modo === 'local' ? 'q' : modo === 'classico' || modo === 'duelo-motores' ? 'n' : 'k'} cor={modo.includes('modelo') || modo === 'benchmark' ? 'b' : 'w'} />
          </div>
          <blockquote>“Cada escolha antes do primeiro lance já faz parte da partida.”</blockquote>
        </aside>
        <div className="formulario-preparacao">
          <div className="cabecalho-formulario">
            <span>
              {modo === 'local'
                ? 'MESMO APARELHO'
                : modo === 'classico'
                  ? 'CONTRA O COMPUTADOR'
                  : modo === 'duelo-motores'
                    ? 'CPU × CPU'
                    : modo === 'duelo-modelos'
                      ? 'LAB · MODELO × MODELO'
                      : modo === 'benchmark'
                        ? 'LAB · SÉRIE'
                        : modo === 'modelo'
                          ? 'LAB · MODELO'
                          : 'PREPARAÇÃO'}
            </span>
            <h1>{titulos[modo].titulo}</h1>
            <p>{titulos[modo].frase}</p>
          </div>

          {!duelo &&
            (modo === 'local' ? (
              <section className="grupo-preparacao">
                <div className="titulo-grupo">
                  <span>01</span>
                  <div>
                    <strong>Jogadores</strong>
                    <small>Os nomes exibidos junto aos relógios</small>
                  </div>
                </div>
                <div className="duas-colunas">
                  <label className="campo-regio">
                    <span>Brancas</span>
                    <input value={nomeUm} maxLength={28} onChange={(e) => setNomeUm(e.target.value)} />
                  </label>
                  <label className="campo-regio">
                    <span>Pretas</span>
                    <input value={nomeDois} maxLength={28} onChange={(e) => setNomeDois(e.target.value)} />
                  </label>
                </div>
              </section>
            ) : (
              <section className="grupo-preparacao">
                <div className="titulo-grupo">
                  <span>01</span>
                  <div>
                    <strong>Sua cor</strong>
                    <small>Escolha de que lado estará o tabuleiro</small>
                  </div>
                </div>
                <div className="seletor-segmentado tres">
                  <button className={cor === 'w' ? 'selecionado' : ''} onClick={() => setCor('w')} type="button">
                    Brancas
                  </button>
                  <button className={cor === 'b' ? 'selecionado' : ''} onClick={() => setCor('b')} type="button">
                    Pretas
                  </button>
                  <button className={cor === 'aleatoria' ? 'selecionado' : ''} onClick={() => setCor('aleatoria')} type="button">
                    Aleatória
                  </button>
                </div>
              </section>
            ))}

          <section className="grupo-preparacao">
            <div className="titulo-grupo">
              <span>{duelo ? '01' : '02'}</span>
              <div>
                <strong>Ritmo da partida</strong>
                <small>Tempo inicial e incremento por lance</small>
              </div>
            </div>
            <label className="campo-regio">
              <span>Controle de tempo</span>
              <select value={controleId} onChange={(e) => setControleId(e.target.value)}>
                {CONTROLES_TEMPO.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>
            {controleId === 'personalizado' && (
              <div className="duas-colunas campos-personalizados">
                <label className="campo-regio">
                  <span>Minutos iniciais</span>
                  <input type="number" inputMode="numeric" min="1" max="180" value={minutosPersonalizados} onChange={(e) => setMinutosPersonalizados(Number(e.target.value))} />
                  <small>De 1 a 180 minutos</small>
                </label>
                <label className="campo-regio">
                  <span>Incremento por lance</span>
                  <div className="campo-com-unidade">
                    <input type="number" inputMode="numeric" min="0" max="60" value={incrementoPersonalizado} onChange={(e) => setIncrementoPersonalizado(Number(e.target.value))} />
                    <b>segundos</b>
                  </div>
                  <small>De 0 a 60 segundos</small>
                </label>
              </div>
            )}
          </section>

          {modo === 'classico' && (
            <section className="grupo-preparacao">
              <div className="titulo-grupo">
                <span>03</span>
                <div>
                  <strong>Nível da CPU</strong>
                  <small>Stockfish no servidor · só escolha a dificuldade</small>
                </div>
              </div>
              <div className="grade-niveis">
                {niveis.map((n) => (
                  <button type="button" key={n.id} onClick={() => setNivel(n.id)} className={nivel === n.id ? 'nivel-ativo' : ''}>
                    <strong>{n.nome}</strong>
                    <small>{n.detalhe}</small>
                  </button>
                ))}
              </div>
            </section>
          )}

          {modo === 'modelo' && (
            <section className="grupo-preparacao">
              <div className="titulo-grupo">
                <span>03</span>
                <div>
                  <strong>Modelo convidado</strong>
                  <small>Somente modelos liberados na administração</small>
                </div>
              </div>
              {disponiveis.length ? (
                <>
                  <label className="campo-regio">
                    <span>Provedor e modelo</span>
                    <select value={modeloId} onChange={(e) => setModeloId(e.target.value)}>
                      {disponiveis.map((m) => (
                        <option key={m.idPublico} value={m.idPublico}>
                          {m.provedor} · {m.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="seletor-segmentado quatro">
                    {estilos.map((item) => (
                      <button type="button" key={item.id} className={estilo === item.id ? 'selecionado' : ''} onClick={() => setEstilo(item.id)}>
                        {item.nome}
                      </button>
                    ))}
                  </div>
                  <div className="titulo-grupo" style={{ marginTop: '1rem' }}>
                    <span>Apoio</span>
                    <div>
                      <strong>Assistência do Stockfish</strong>
                      <small>Quão rigoroso é o filtro de qualidade</small>
                    </div>
                  </div>
                  <div className="grade-niveis">
                    {assistencias.map((a) => (
                      <button type="button" key={a.id} className={assistencia === a.id ? 'nivel-ativo' : ''} onClick={() => setAssistencia(a.id)}>
                        <strong>{a.nome}</strong>
                        <small>{a.detalhe}</small>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="estado-sem-modelos">
                  <Icone nome="modelo" tamanho={32} />
                  <strong>Nenhum modelo liberado</strong>
                  <p>Abra a administração e libere modelos para partidas.</p>
                  <button type="button" className="botao-contorno" onClick={aoAdministrar}>
                    Abrir administração
                  </button>
                </div>
              )}
            </section>
          )}

          {cpuVsCpu && (
            <>
              <section className="grupo-preparacao">
                <div className="titulo-grupo">
                  <span>03</span>
                  <div>
                    <strong>CPU A · Brancas</strong>
                    <small>Nível do Stockfish que joga de brancas</small>
                  </div>
                </div>
                <div className="grade-niveis">
                  {niveis.map((n) => (
                    <button type="button" key={n.id} onClick={() => setNivelA(n.id)} className={nivelA === n.id ? 'nivel-ativo' : ''}>
                      <strong>{n.nome}</strong>
                      <small>{n.detalhe}</small>
                    </button>
                  ))}
                </div>
              </section>
              <section className="grupo-preparacao">
                <div className="titulo-grupo">
                  <span>04</span>
                  <div>
                    <strong>CPU B · Pretas</strong>
                    <small>Nível do Stockfish que joga de pretas</small>
                  </div>
                </div>
                <div className="grade-niveis">
                  {niveis.map((n) => (
                    <button type="button" key={n.id} onClick={() => setNivelB(n.id)} className={nivelB === n.id ? 'nivel-ativo' : ''}>
                      <strong>{n.nome}</strong>
                      <small>{n.detalhe}</small>
                    </button>
                  ))}
                </div>
              </section>
              <p className="preview-duelo" aria-live="polite">
                <strong>Confronto:</strong>
                <span>Brancas · Stockfish · {niveis.find((n) => n.id === nivelA)?.nome}</span>
                <span>Pretas · Stockfish · {niveis.find((n) => n.id === nivelB)?.nome}</span>
              </p>
            </>
          )}

          {duelo && !cpuVsCpu && (
            <>
              {blocoLado('A', 'Brancas', tipoA, setTipoA, nivelA, setNivelA, modeloA, setModeloA, estiloA, setEstiloA, tiposPermitidosA)}
              <div className="acoes-troca-duelo">
                <button type="button" className="botao-contorno" onClick={trocarParticipantes} aria-label="Trocar participantes A e B">
                  <Icone nome="girar" tamanho={18} /> Trocar A ↔ B (cores)
                </button>
                <p className="preview-duelo" aria-live="polite">
                  <strong>Ordem no tabuleiro:</strong>
                  <span>Brancas · {previewA}</span>
                  <span>Pretas · {previewB}</span>
                </p>
              </div>
              {blocoLado('B', 'Pretas', tipoB, setTipoB, nivelB, setNivelB, modeloB, setModeloB, estiloB, setEstiloB, tiposPermitidosB)}
              <section className="grupo-preparacao">
                <div className="titulo-grupo">
                  <span>Limites</span>
                  <div>
                    <strong>Segurança da partida</strong>
                    <small>Evita jogos sem fim</small>
                  </div>
                </div>
                <label className="campo-regio">
                  <span>Limite de lances (meio-movimentos)</span>
                  <input type="number" min={20} max={600} value={limiteLances} onChange={(e) => setLimiteLances(Number(e.target.value))} />
                </label>
                {modo === 'benchmark' && (
                  <>
                    <label className="campo-regio" style={{ marginTop: '0.75rem' }}>
                      <span>Partidas na série</span>
                      <input type="number" min={1} max={32} value={partidasSerie} onChange={(e) => setPartidasSerie(Number(e.target.value))} />
                    </label>
                    <label className="linha-interruptor" style={{ marginTop: '0.75rem' }}>
                      <span>
                        <strong>Inverter cores a cada partida</strong>
                        <small>A e B trocam de lado para equidade</small>
                      </span>
                      <input type="checkbox" checked={inverterCores} onChange={(e) => setInverterCores(e.target.checked)} />
                      <i />
                    </label>
                  </>
                )}
              </section>
              {modo === 'duelo-modelos' && !disponiveis.length && (
                <div className="estado-sem-modelos">
                  <strong>Libere pelo menos um modelo</strong>
                  <button type="button" className="botao-contorno" onClick={aoAdministrar}>
                    Administração
                  </button>
                </div>
              )}
            </>
          )}

          {!duelo && (
            <section className="grupo-preparacao opcoes-finais">
              <label className="linha-interruptor">
                <span>
                  <strong>Permitir desfazer</strong>
                  <small>{modo === 'local' ? 'Volta um lance' : 'Volta a última rodada completa'}</small>
                </span>
                <input type="checkbox" checked={desfazer} onChange={(e) => setDesfazer(e.target.checked)} />
                <i />
              </label>
              {modo === 'local' && (
                <label className="linha-interruptor">
                  <span>
                    <strong>Virar após cada lance</strong>
                    <small>O lado ativo fica sempre na base</small>
                  </span>
                  <input type="checkbox" checked={inverter} onChange={(e) => setInverter(e.target.checked)} />
                  <i />
                </label>
              )}
            </section>
          )}

          <button
            type="button"
            className="botao-iniciar"
            disabled={
              (modo === 'modelo' && !modeloId) ||
              (modo === 'classico' && motorLocal === 'lc0' && !lc0?.disponivel) ||
              (duelo && !cpuVsCpu && (!podeIniciarDuelo || !modelosOk))
            }
            onClick={iniciar}
          >
            <span>
              {cpuVsCpu
                ? 'Iniciar confronto'
                : duelo
                  ? modo === 'benchmark'
                    ? 'Iniciar série'
                    : 'Iniciar duelo'
                  : 'Preparar tabuleiro'}
            </span>
            <Icone nome="avancar" />
          </button>
        </div>
      </section>
    </main>
  );
}
